import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Shared-rig contract: forbidden. A thermal camera does not see light. A wash
// would be invisible to it, and drawing one anyway would put a colour on
// screen that is not a temperature.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'thermal',
  family: 'object',
  danceability: 0.40,
  energyLevel: 0.68,
  bpmMin: 100,
  bpmMax: 170,
  movement: 0.60,
  tonality: 0.45,
  prefers: { gridStrength: [0.55, 1.0], percussiveness: [0.40, 1.0] },
  enabled: true
};

// Thermal: geometric solids in a cold room, seen through an infrared imager.
// They heat on the beats and cool between them, in the ironbow ramp.
//
// THE COLOUR IS NEVER CHOSEN, IT IS THE TEMPERATURE. So the scene is not drawn
// in colour at all. The room, the objects, their footprints on the floor, the
// heat haze and the bar's wave write a TEMPERATURE - one scalar - into a
// private 8-bit target, combined with MAX blending (the hottest thing along a
// ray wins, and a cool wisp can never cool a hot object behind it). One final
// full-frame pass does what a real imager does to that scalar: the germanium
// lens's slight blur and vignetting, the sensor's fine noise, the 32-level
// quantisation that makes the bands, and then - only then - the ironbow
// lookup. Every one of them acts on the temperature, so none of them can put
// a colour on screen that is not on the ramp. The composer's own ACES curve,
// bloom, aberration, vignette and grain would, so this visualizer asks for
// none of them (postProcessing, gallery.js's contract). The only exceptions
// are the brief's: the album tints the background's near-black by 5%, and the
// imager's readouts are white text over the picture.
//
// A THERMAL IMAGE READS ITS VOLUME FROM TEMPERATURE. There is no light and no
// shading here, so relief has to come from heat: faces turned toward the heat
// source (above, a little in front) run 0.15 warmer, faces turned away 0.10
// colder, sharp edges 0.12 warmer on a band three pixels wide - and each object
// has a CORE and a SURFACE. Heat arrives at the surface, soaks into the core,
// and on the way down the surface cools first: after a peak the rim of an
// object darkens while its centre stays warm, which is what makes a massive
// object read as massive.
//
// THE SIGNATURE IS DIFFERENTIAL COOLING. Each object's thermal mass follows
// its size: small ones heat and cool fast, big ones slowly - StrobeWall's
// filament inertia with a time constant per object. After a drop everything
// is white; a second later the small ones are dark while the big ones are
// still red.

// --- the ramp -------------------------------------------------------------------
// Display (sRGB) values; the texture is declared sRGB and the output is not
// tone mapped, so these are the colours on screen.
const IRONBOW = [
  [0.00, [0.000, 0.000, 0.000]],
  [0.15, [0.180, 0.020, 0.360]],   // violet profond
  [0.35, [0.700, 0.050, 0.560]],   // magenta
  [0.50, [0.900, 0.120, 0.120]],   // rouge
  [0.65, [1.000, 0.500, 0.020]],   // orange
  [0.80, [1.000, 0.850, 0.100]],   // jaune
  [0.92, [1.000, 0.970, 0.760]],   // blanc chaud
  [1.00, [1.000, 1.000, 1.000]]    // blanc
];
export function ironbow(t) {
  t = Math.max(0, Math.min(1, t));
  for (let i = 1; i < IRONBOW.length; i++) {
    if (t <= IRONBOW[i][0]) {
      const [t0, a] = IRONBOW[i - 1], [t1, b] = IRONBOW[i];
      const k = (t - t0) / (t1 - t0);
      return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k];
    }
  }
  return IRONBOW[IRONBOW.length - 1][1].slice();
}

// The scale the bar reads, and the spot readout: t = 0 is 18 degrees, t = 1 is 42.
export const TEMP_MIN = 18.0, TEMP_MAX = 42.0;
const enDegres = (t) => TEMP_MIN + (TEMP_MAX - TEMP_MIN) * Math.max(0, Math.min(1, t));

const T_BG = 0.08;               // « une caméra thermique n'a jamais de vrai noir »
const T_ROOM = 0.125;            // the room's walls and floor: ironbow(0.10 to 0.14)
const T_DECOR = 0.05;            // the column and the ledge: very cold, in silhouette
const QUANT = 32;
const TINT_BG = 0.05;
const NOISE = 0.010;             // the sensor's thermal noise, in temperature
const LENS_PX = 1.25;            // the germanium lens's blur, in sensor pixels
const LENS_VIGNETTE = 0.04;      // the lens reads a little colder toward the corners
const PLANCHER_PARTAGE = 0.32, PLANCHER = 0.50;

// --- the objects ------------------------------------------------------------------
const IMPULSE_BEAT = 0.40;
// The bar's wave reaches EVERY object. At 0.22 every two seconds the big ones
// never got back down and there was nothing cold left to cool differentially.
const IMPULSE_WAVE = 0.12;
// THE TIME CONSTANTS ARE THE PIECE. Cooling from white, an object is back at
// rest after 3.8 tau and still red until 0.81 tau, so the big tau has to be
// more than 4.7x the small one for "small dark before big leaves red".
const TAU_COOL = [0.35, 2.8];
const TAU_HEAT = [0.035, 0.16];
// The core soaks the surface's heat with its own time constant, longer for the
// massive ones; the surface gives up this share of what the core takes.
const TAU_CORE = [0.12, 2.2];
const CORE_SHARE = 0.35;
const FACE_HOT = 0.15, FACE_COLD = 0.10;
// THE EDGE IS +0.12 ON THE IMAGE, which takes +0.22 in the shader. The sensor
// is half the screen's resolution, so a 3-pixel band is 1.5 sensor pixels,
// and the lens blur spreads half of it away: written at +0.12, a profile across
// 168 vertices read +0.06 at the median. On a 2-sensor-pixel band, +0.22 in the
// shader read +0.105 on screen; the 4x MSAA resolve softens it again (+0.087),
// so it is written at +0.27.
const EDGE_HOT = 0.27;
const EDGE_BAND_PX = 3;          // screen pixels, along each sharp edge
const DIFFUSE_RADIUS = 3.6;
const DIFFUSE_RATE = 0.18;       // per second, at contact, for a unit mass
// THE SIZE IS SET ON SCREEN: 8 to 18% of the frame's height at the object's
// own depth. In world units it was 5% - measured on the capture - and at that
// size neither the edges nor the faces had room to read.
const SIZE_ECRAN = [0.08, 0.18];
const ORBIT_FX = [0.40, 0.92];   // of the frame's half width at the origin
const ORBIT_FY = [0.30, 0.78];   // of the half height
const ORBIT_Z = [-4.5, 3.0];     // depths: different ones, the brief asks
const ORBIT_SPEED = [0.05, 0.13]; // rad/s
const ORBIT_TILT = 0.30;
const REORG_BEATS = 2;

const CAMERA_DIST = 12.5;
const CAMERA_SWEEP = 0.45;        // rad, the orbit's half arc
const CAMERA_PERIOD = 90;         // s
const SHAKE_POS = 0.035, SHAKE_ROT = 0.0035;

// --- the room -----------------------------------------------------------------------
const FLOOR_Y = -4.8;
const ROOM_BACK_Z = -11;          // the back wall
const ROOM_FRONT_Z = 9;           // how far the floor comes toward the camera
const ROOM_H = 24;                // the walls' height above the floor: past the frame's top
const ROOM_CORNER = 2.6;          // corners are colder over this distance
const ROOM_WARM_R = 2.8;          // the floor and walls warm near an object

// --- the footprints -------------------------------------------------------------------
// A STAMP, not a smear: the footprint is the object's own shape, flattened onto
// the floor under it - the GPU rasterises its exact projection - dropped once
// in a while as the object passes low, and cooling in six seconds. The first
// version accumulated heat continuously into a coarse grid: a moving object
// drew a long blurred streak that read as a reflection.
const STAMP_N = 30;
const STAMP_REACH = 1.8;          // how close above the floor an object must pass
const STAMP_EVERY = 0.45;         // s between two stamps of one object
const STAMP_LIFE = 6.0;

const HAZE_N = 260;
// THE TRAIL MUST LEAVE THE OBJECT. The objects move 0.4 to 1 unit a second and
// are 1 to 2 units wide: a trail born at the object's centre and living two
// seconds stayed hidden behind the object that made it - 2,284 sprites emitted
// in twelve seconds, none visible. It is born at the TRAILING edge, and it
// rises fast enough to clear the object: warm air goes up.
const HAZE_LIFE = 2.2;
const HAZE_OVER = 0.10;           // only an object this much above the room leaves a trail
const HAZE_EVERY = 0.07;          // s between two sprites of one object
const HAZE_RISE = 0.85;           // world units per second: warm air rises

// THE SENSOR IS HALF THE SCREEN'S RESOLUTION, and that is the register as much
// as the saving: an infrared imager is 320 to 640 pixels wide and its picture
// is soft. Measured on the Pi, the full-resolution target cost 1.8 ms at tier 0
// and 5.1 ms at tier 2. The quantisation, the noise and the ramp still run at
// the screen's resolution, so the bands stay sharp.
// The quantisation and the lens vignetting run on EVERY tier - a floor and a
// multiply-add - so the bands are there whatever the Pi's governor chose;
// the lens blur and the noise are the tiers 1-2 artefacts.
const TIERS = {
  2: { objects: 14, haze: true, stamps: true, artefacts: true, wave: true, capteur: 0.5 },
  1: { objects: 12, haze: false, stamps: false, artefacts: true, wave: true, capteur: 0.5 },
  0: { objects: 10, haze: false, stamps: false, artefacts: false, wave: false, capteur: 0.5 }
};

const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);
const lerp = (a, b, t) => a + (b - a) * t;
function urlParam(nom) {
  if (typeof window === 'undefined' || !window.location) return null;
  return new URLSearchParams(window.location.search).get(nom);
}

// --- the edges ----------------------------------------------------------------
// The shader finds edges from a barycentric attribute - but a triangulated face
// has a DIAGONAL that is not an edge. So each triangle edge is classified
// first: real if only one triangle owns it or if the two owners' normals differ
// by more than EDGE_ANGLE; a fake edge gets 1 added to its barycentric
// component, so the shader never finds it small. 32 degrees separates the
// icosahedron's 41.8 from the torus's 22.5 and the cylinder's 15.
const EDGE_ANGLE = 32 * Math.PI / 180;
export function avecAretes(geo) {
  const g = geo.index ? geo.toNonIndexed() : geo.clone();
  g.computeVertexNormals();
  const P = g.getAttribute('position').array;
  const nTri = P.length / 9;
  const fn = new Float32Array(nTri * 3);
  const v = (i) => [P[i * 3], P[i * 3 + 1], P[i * 3 + 2]];
  for (let t = 0; t < nTri; t++) {
    const a = v(t * 3), b = v(t * 3 + 1), c = v(t * 3 + 2);
    const u = [b[0] - a[0], b[1] - a[1], b[2] - a[2]], w = [c[0] - a[0], c[1] - a[1], c[2] - a[2]];
    const n = [u[1] * w[2] - u[2] * w[1], u[2] * w[0] - u[0] * w[2], u[0] * w[1] - u[1] * w[0]];
    const l = Math.hypot(n[0], n[1], n[2]) || 1;
    fn[t * 3] = n[0] / l; fn[t * 3 + 1] = n[1] / l; fn[t * 3 + 2] = n[2] / l;
  }
  // Rounded, and -0 folded into 0: where a torus or a cylinder closes on
  // itself, sin(2 pi) is -2e-16, which toFixed writes "-0.0000" - the two sides
  // of the seam got different keys, and the seam was counted as an edge.
  const r4 = (x) => { const y = Math.round(x * 1e4) / 1e4; return (y === 0 ? 0 : y).toString(); };
  const k = (i) => r4(P[i * 3]) + ',' + r4(P[i * 3 + 1]) + ',' + r4(P[i * 3 + 2]);
  const cle = (i, j) => { const a = k(i), b = k(j); return a < b ? a + '|' + b : b + '|' + a; };
  const aretes = new Map();
  for (let t = 0; t < nTri; t++) for (let e = 0; e < 3; e++) {
    const c = cle(t * 3 + (e + 1) % 3, t * 3 + (e + 2) % 3);   // the edge opposite vertex e
    if (!aretes.has(c)) aretes.set(c, []);
    aretes.get(c).push(t);
  }
  const bary = new Float32Array(nTri * 9);
  let reelles = 0;
  for (let t = 0; t < nTri; t++) {
    for (let i = 0; i < 3; i++) bary[t * 9 + i * 3 + i] = 1;
    for (let e = 0; e < 3; e++) {
      const own = aretes.get(cle(t * 3 + (e + 1) % 3, t * 3 + (e + 2) % 3));
      let reel = own.length < 2;
      if (!reel) {
        const o = own[0] === t ? own[1] : own[0];
        const d = fn[t * 3] * fn[o * 3] + fn[t * 3 + 1] * fn[o * 3 + 1] + fn[t * 3 + 2] * fn[o * 3 + 2];
        reel = Math.acos(Math.max(-1, Math.min(1, d))) > EDGE_ANGLE;
      }
      if (reel) reelles++;
      else for (let i = 0; i < 3; i++) bary[t * 9 + i * 3 + e] += 1;
    }
  }
  g.setAttribute('aBary', new THREE.BufferAttribute(bary, 3));
  g.userData.aretesReelles = reelles / 2;
  // normalised to an extent of 1, so a scale IS a size
  g.computeBoundingBox();
  const bb = g.boundingBox, ext = Math.max(bb.max.x - bb.min.x, bb.max.y - bb.min.y, bb.max.z - bb.min.z);
  const cx = (bb.max.x + bb.min.x) / 2, cy = (bb.max.y + bb.min.y) / 2, cz = (bb.max.z + bb.min.z) / 2;
  g.translate(-cx, -cy, -cz);
  g.scale(1 / ext, 1 / ext, 1 / ext);
  g.computeBoundingSphere();
  return g;
}

// --- shaders ----------------------------------------------------------------------

const OBJ_VERT = `
  precision mediump float;
  attribute vec3 aBary;
  varying vec3 vBary;
  varying vec3 vN;
  varying vec3 vW;
  void main() {
    vBary = aBary;
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vW = wp.xyz;
    vN = normalize(mat3(modelMatrix) * normal);
    gl_Position = projectionMatrix * viewMatrix * wp;
  }
`;
const OBJ_FRAG = `
  precision mediump float;
  uniform float uTs;          // surface temperature
  uniform float uTc;          // core temperature
  uniform vec3 uCentre;       // world
  uniform float uRayon;       // world, bounding radius
  uniform vec3 uHeatDir;      // world, toward the heat source
  uniform float uBandePx;     // the edge band's width, in sensor pixels
  varying vec3 vBary;
  varying vec3 vN;
  varying vec3 vW;
  void main() {
    vec3 n = normalize(vN);
    vec3 v = normalize(cameraPosition - vW);
    // CORE AND SURFACE: the projected distance from the object's centre. The
    // middle of the silhouette shows the core, its rim the surface.
    vec3 q = vW - uCentre;
    float r = clamp(length(q - v * dot(q, v)) / uRayon, 0.0, 1.0);
    float t = mix(uTc, uTs, smoothstep(0.15, 0.95, r));
    // VOLUME: toward the heat source warmer, away from it colder
    float f = dot(n, uHeatDir);
    t += ${FACE_HOT.toFixed(2)} * max(f, 0.0) - ${FACE_COLD.toFixed(2)} * max(-f, 0.0);
    // SHARP EDGES, a flat band at a constant width on screen: the barycentric
    // distance divided by its own screen-space rate of change
    vec3 d = vBary / max(fwidth(vBary), vec3(1e-4));
    float m1 = min(d.x, min(d.y, d.z));
    t += ${EDGE_HOT.toFixed(2)} * (1.0 - smoothstep(uBandePx - 0.5, uBandePx + 0.5, m1));
    gl_FragColor = vec4(vec3(clamp(t, 0.0, 1.0)), 1.0);
  }
`;

// The room: floor, back wall, two side walls, at T_ROOM, colder toward the
// corners, warmer near the objects. ALL OF IT PER VERTEX, on subdivided planes:
// the warmth varies over 2.8 units and the corners over 2.6, so the vertices
// carry it exactly, and the fragment only interpolates. Per pixel, the loop
// over fourteen objects cost 7 ms at tier 1 on the Pi, for a field that has
// no detail finer than the grid. uSurf tells which plane this is, so a point's
// distance to its OWN plane - always zero - is left out of the corners.
const ROOM_VERT = `
  precision mediump float;
  uniform float uSurf;        // 0 floor, 1 back wall, 2 side walls, 3 decor
  uniform float uBase;
  uniform float uWallX;
  uniform vec4 uObj[14];      // xyz, temperature excess over the room
  uniform vec3 uHeatDir;
  varying float vT;
  void main() {
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vec3 w = wp.xyz;
    float t = uBase;
    if (uSurf < 2.5) {
      float c = 0.0;
      if (uSurf != 0.0) c += exp(-(w.y - ${FLOOR_Y.toFixed(2)}) / ${ROOM_CORNER.toFixed(2)});
      if (uSurf != 1.0) c += exp(-(w.z - ${ROOM_BACK_Z.toFixed(2)}) / ${ROOM_CORNER.toFixed(2)});
      if (uSurf != 2.0) c += exp(-(uWallX - abs(w.x)) / ${ROOM_CORNER.toFixed(2)});
      t -= 0.022 * min(c, 1.4);
      if (uSurf == 0.0) t += 0.012 * clamp((w.z - ${ROOM_BACK_Z.toFixed(2)}) / 18.0, 0.0, 1.0);
      float warm = 0.0;
      for (int i = 0; i < 14; i++) {
        vec3 d = w - uObj[i].xyz;
        warm += uObj[i].w * exp(-dot(d, d) / ${(2 * ROOM_WARM_R * ROOM_WARM_R).toFixed(2)});
      }
      t += 0.10 * min(warm, 1.5);
    } else {
      t += 0.012 * dot(normalize(mat3(modelMatrix) * normal), uHeatDir);
    }
    vT = t;
    gl_Position = projectionMatrix * viewMatrix * wp;
  }
`;
const ROOM_FRAG = `
  precision mediump float;
  varying float vT;
  void main() { gl_FragColor = vec4(vec3(clamp(vT, 0.0, 1.0)), 1.0); }
`;
const STAMP_FRAG = `
  precision mediump float;
  uniform float uT;
  void main() { gl_FragColor = vec4(vec3(uT), 1.0); }
`;
const PLAIN_VERT = `
  precision mediump float;
  void main() { gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
`;

// The haze and the bar's wave: soft temperature, MAX-blended.
const HAZE_VERT = `
  precision mediump float;
  attribute float aT;
  attribute float aSize;
  uniform float uPxPerUnit;
  varying float vT;
  void main() {
    vT = aT;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mv;
    gl_PointSize = aT > 0.0 ? aSize * uPxPerUnit / max(-mv.z, 0.5) : 0.0;
  }
`;
const HAZE_FRAG = `
  precision mediump float;
  varying float vT;
  void main() {
    float r = length(gl_PointCoord - 0.5) * 2.0;
    if (r > 1.0) discard;
    gl_FragColor = vec4(vec3(vT * exp(-r * r * 3.2)), 1.0);
  }
`;
const RING_FRAG = `
  precision mediump float;
  uniform float uT;
  uniform float uThick;
  varying vec2 vUv;
  void main() {
    float r = length(vUv - 0.5) * 2.0;
    float s = exp(-pow((r - (1.0 - uThick)) / uThick, 2.0) * 2.0);
    if (r > 1.0) s = 0.0;
    gl_FragColor = vec4(vec3(uT * s), 1.0);
  }
`;
const UV_VERT = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// THE IMAGER. Temperature to ironbow, in the order a camera applies them.
const DISPLAY_VERT = `
  precision mediump float;
  varying vec2 vUv;
  varying vec2 vNdc;
  void main() {
    vUv = uv;
    vNdc = position.xy * 2.0;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;
const DISPLAY_FRAG = `
  precision mediump float;
  uniform sampler2D uTemp;
  uniform sampler2D uRamp;
  uniform vec2 uTexel;
  uniform float uArtefacts;
  uniform float uTime;
  uniform vec3 uTint;
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  varying vec2 vNdc;

  float zone(vec2 s) {
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float a = mix(${PLANCHER_PARTAGE.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
    return ${PLANCHER.toFixed(2)} + (a - ${PLANCHER_PARTAGE.toFixed(2)})
         * ${((1 - PLANCHER) / (1 - PLANCHER_PARTAGE)).toFixed(6)};
  }
  float hash(vec2 p) {
    p = fract(p * vec2(0.1031, 0.1030));
    p += dot(p, p.yx + 33.33);
    return fract((p.x + p.y) * p.x);
  }

  void main() {
    float t;
    if (uArtefacts > 0.5) {
      vec2 o = uTexel * ${LENS_PX.toFixed(2)};
      t = texture2D(uTemp, vUv).r * 0.40
        + (texture2D(uTemp, vUv + vec2(o.x, 0.0)).r + texture2D(uTemp, vUv - vec2(o.x, 0.0)).r
         + texture2D(uTemp, vUv + vec2(0.0, o.y)).r + texture2D(uTemp, vUv - vec2(0.0, o.y)).r) * 0.15;
      vec2 fc = floor(gl_FragCoord.xy);
      t += (hash(fc + vec2(mod(uTime * 61.0, 97.0), mod(uTime * 37.0, 89.0))) - 0.5) * ${(2 * NOISE).toFixed(3)};
      t += (hash(vec2(fc.x, 7.0)) - 0.5) * ${(NOISE * 0.6).toFixed(4)};
    } else {
      t = texture2D(uTemp, vUv).r;
    }
    // THE LENS'S VIGNETTING, in temperature: dimming the colour would leave the ramp
    vec2 c = vNdc * vec2(uAspect, 1.0) / max(uAspect, 1.0);
    t -= ${LENS_VIGNETTE.toFixed(3)} * dot(c, c);
    // the safe zone pulls toward the room, on the temperature too
    t = ${T_ROOM.toFixed(3)} + (t - ${T_ROOM.toFixed(3)}) * zone(vNdc * vec2(uAspect, 1.0));
    t = (floor(clamp(t, 0.0, 1.0) * ${QUANT.toFixed(1)}) + 0.5) / ${QUANT.toFixed(1)};
    vec3 col = texture2D(uRamp, vec2(t, 0.5)).rgb;
    col = mix(col, uTint, ${TINT_BG.toFixed(2)} * (1.0 - smoothstep(${T_BG.toFixed(2)}, ${(T_ROOM + 0.02).toFixed(3)}, t)));
    gl_FragColor = vec4(col, 1.0);
  }
`;

// THE SCALE BAR: the ramp itself, graduated, in the ramp's own black and white.
// ITS OWN SMALL MESH, not a branch of the full-frame pass: on the Pi's GPU the
// branches of that pass run on every pixel, and the bar cost 2.3 ms there.
const BAR_X0 = 0.915, BAR_X1 = 0.945, BAR_Y0 = -0.62, BAR_Y1 = 0.62;
const BAR_ZONE = [BAR_X0 - 0.03, BAR_X1 + 0.005, BAR_Y0 - 0.01, BAR_Y1 + 0.01];
const BAR_VERT = `
  precision mediump float;
  varying vec2 vNdc;
  void main() {
    vec2 a = vec2(${BAR_ZONE[0].toFixed(3)}, ${BAR_ZONE[2].toFixed(3)});
    vec2 b = vec2(${BAR_ZONE[1].toFixed(3)}, ${BAR_ZONE[3].toFixed(3)});
    vNdc = mix(a, b, position.xy + 0.5);
    gl_Position = vec4(vNdc, 0.0, 1.0);
  }
`;
const BAR_FRAG = `
  precision mediump float;
  uniform sampler2D uRamp;
  uniform vec2 uPx;           // one screen pixel, in NDC
  varying vec2 vNdc;
  void main() {
    float x = vNdc.x, y = vNdc.y;
    float x0 = ${BAR_X0.toFixed(3)}, x1 = ${BAR_X1.toFixed(3)}, y0 = ${BAR_Y0.toFixed(3)}, y1 = ${BAR_Y1.toFixed(3)};
    float t = -1.0;
    if (x > x0 && x < x1 && y > y0 && y < y1) {
      t = (floor((y - y0) / (y1 - y0) * ${QUANT.toFixed(1)}) + 0.5) / ${QUANT.toFixed(1)};
      if (x < x0 + uPx.x || x > x1 - uPx.x || y < y0 + uPx.y || y > y1 - uPx.y) t = 0.0;
    }
    // graduations: sixteen, a long one every four - the five that carry a value
    if (x > x0 - 0.020 && x <= x0 && y > y0 - uPx.y && y < y1 + uPx.y) {
      float g = (y - y0) / (y1 - y0) * 16.0;
      float tick = abs(fract(g + 0.5) - 0.5) * (y1 - y0) / 16.0;
      float big = abs(fract(g / 4.0 + 0.5) - 0.5) < 0.03 ? 1.0 : 0.0;
      if (tick < uPx.y * 0.6 && (big > 0.5 || x > x0 - 0.009)) t = 1.0;
    }
    if (t < 0.0) discard;
    gl_FragColor = vec4(texture2D(uRamp, vec2(t, 0.5)).rgb, 1.0);
  }
`;

// THE READOUTS: text drawn on a canvas at the screen's own pixel density and
// shown 1:1 on a quad placed in pixels, so a glyph lands on whole pixels. The
// first version drew 22 px glyphs into a 128 x 64 canvas stretched over the
// frame and thresholded it: a broken bitmap.
const TEXT_VERT = `
  precision mediump float;
  uniform vec4 uRect;         // x0, y0, w, h in NDC
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(uRect.xy + (position.xy + 0.5) * uRect.zw, 0.0, 1.0);
  }
`;
const TEXT_FRAG = `
  precision mediump float;
  uniform sampler2D uTex;
  uniform float uOpacity;
  varying vec2 vUv;
  void main() {
    float a = texture2D(uTex, vUv).a * uOpacity;
    if (a < 0.004) discard;
    gl_FragColor = vec4(vec3(1.0), a);
  }
`;
const POLICE = '"DejaVu Sans Mono", Menlo, Consolas, "Liberation Mono", ui-monospace, monospace';

// ---------------------------------------------------------------------------------

const _taille = new THREE.Vector2();

export class ThermalVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._plan = Object.assign({}, TIERS[this._tier] || TIERS[1]);
    if (o.capteur != null) this._plan.capteur = o.capteur;   // the bench's knobs
    this._samples = o.samples != null ? o.samples : 4;
    const h = parseInt(urlParam('thermheat'), 10);
    this._chaufferForce = Number.isFinite(h) ? h : null;
    this._dropForce = urlParam('thermall') === '1';
    this._barre = urlParam('thermscale') !== '0';
    this._postProcessing = {
      toneMapping: THREE.NoToneMapping, toneMappingExposure: 1.0,
      bloomStrength: 0.0, aberration: 0.0, vignette: 0.0, grain: 0.0
    };
  }

  get postProcessing() { return this._postProcessing; }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._temps = 0;
    this._graine = 11;
    this._prive = new THREE.Scene();
    this._rt = new THREE.WebGLRenderTarget(4, 4, {
      type: THREE.UnsignedByteType, format: THREE.RGBAFormat,
      minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: true,
      // 4x MSAA: at half resolution a bare silhouette is a staircase of 2 px
      // steps. Each sample keeps its MAX blend, the resolve averages the
      // coverage, so a border pixel gets an in-between TEMPERATURE - still a
      // ramp colour after the lookup.
      samples: this._samples
    });
    // the heat source: above, a little in front
    this._heatDir = new THREE.Vector3(0, 0.92, 0.38).normalize();
    this._tanH = Math.tan(THREE.MathUtils.degToRad(camera.fov || 50) / 2);
    this._aspect = safeZoneUniforms.uAspect.value || 1.78;

    const ramp = new Uint8Array(256 * 4);
    for (let i = 0; i < 256; i++) {
      const c = ironbow(i / 255);
      ramp[i * 4] = Math.round(c[0] * 255); ramp[i * 4 + 1] = Math.round(c[1] * 255);
      ramp[i * 4 + 2] = Math.round(c[2] * 255); ramp[i * 4 + 3] = 255;
    }
    this._rampTex = new THREE.DataTexture(ramp, 256, 1, THREE.RGBAFormat);
    this._rampTex.colorSpace = THREE.SRGBColorSpace;
    // NEAREST: the blend of two texels is only on the ramp within one stop
    this._rampTex.magFilter = THREE.NearestFilter;
    this._rampTex.minFilter = THREE.NearestFilter;
    this._rampTex.generateMipmaps = false;
    this._rampTex.needsUpdate = true;

    this._objUni = [];
    this._construirePiece();
    this._construireObjets();
    if (this._plan.haze) this._construireBrume();
    if (this._plan.stamps) this._construireEmpreintes();
    if (this._plan.wave) this._construireOnde();

    this._uni = {
      uTemp: { value: this._rt.texture },
      uRamp: { value: this._rampTex },
      uTexel: { value: new THREE.Vector2(1 / 640, 1 / 360) },
      uArtefacts: { value: this._plan.artefacts ? 1 : 0 },
      uTime: { value: 0 },
      uTint: { value: new THREE.Color().setRGB(...ironbow(T_ROOM)) },
      uAspect: safeZoneUniforms.uAspect,
      uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize,
      uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._ecran = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._uni, vertexShader: DISPLAY_VERT, fragmentShader: DISPLAY_FRAG,
      depthTest: false, depthWrite: false, precision: 'mediump'
    }));
    this._ecran.frustumCulled = false;
    this._ecran.renderOrder = -100;
    this._scene.add(this._ecran);
    // THE TEMPERATURE IS RENDERED HERE, just before the display quad samples
    // it, with the camera as it is at this very frame. advect.js's pattern.
    const self = this;
    this._ecran.onBeforeRender = function (renderer) { self._rendreTemperature(renderer); };

    this._barreUni = { uRamp: { value: this._rampTex }, uPx: { value: new THREE.Vector2(2 / 1280, 2 / 720) } };
    this._barreMesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._barreUni, vertexShader: BAR_VERT, fragmentShader: BAR_FRAG,
      depthTest: false, depthWrite: false, precision: 'mediump'
    }));
    this._barreMesh.frustumCulled = false;
    this._barreMesh.renderOrder = -99;
    this._barreMesh.visible = this._barre;
    this._scene.add(this._barreMesh);

    // the readouts: the bar's five values, the header, the aiming cross
    this._textes = {
      echelle: this._creerTexte(0.80, this._barre),
      entete: this._creerTexte(0.60, true),
      visee: this._creerTexte(0.60, true)
    };
    this._pxVus = null;
    this._spot = T_ROOM;
    this._spotAffiche = null;
    this._spotAt = -1;

    this._battements = 0;
    this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null;
    this._libre = 0; this._secondesBeat = 0.5;
    this._modePrec = null;
    this._dropTenue = -1;
    this._air = 0;
    this._onde = null;
    this._paletteVue = undefined;
    this._ambiant = 0.1;
    this._placerCamera(0);
  }

  _hasard() {
    this._graine = (this._graine * 1103515245 + 12345) % 2147483648;
    return this._graine / 2147483648;
  }

  // --- the room --------------------------------------------------------------------
  // A floor running back to a wall, two side walls suggested at the edges, and
  // two very cold pieces of furniture in silhouette for scale. Without them the
  // objects floated in a flat violet void.
  _construirePiece() {
    const HH = (CAMERA_DIST - ROOM_BACK_Z) * this._tanH;   // half height at the back wall
    this._wallX = Math.max(10, HH * this._aspect * 0.88);
    const W = this._wallX;
    const objArr = [];
    for (let i = 0; i < 14; i++) objArr.push(new THREE.Vector4(0, -1000, 0, 0));
    this._roomObj = objArr;
    const mat = (surf, base) => new THREE.ShaderMaterial({
      uniforms: { uSurf: { value: surf }, uBase: { value: base }, uWallX: { value: W },
        uObj: { value: objArr }, uHeatDir: { value: this._heatDir } },
      vertexShader: ROOM_VERT, fragmentShader: ROOM_FRAG, precision: 'mediump', side: THREE.DoubleSide
    });
    const depth = ROOM_FRONT_Z - ROOM_BACK_Z;
    const sol = new THREE.Mesh(new THREE.PlaneGeometry(2 * W, depth, 64, 40), mat(0, T_ROOM + 0.004));
    sol.rotation.x = -Math.PI / 2;
    sol.position.set(0, FLOOR_Y, (ROOM_FRONT_Z + ROOM_BACK_Z) / 2);
    const fond = new THREE.Mesh(new THREE.PlaneGeometry(2 * W, ROOM_H, 48, 24), mat(1, T_ROOM));
    fond.position.set(0, FLOOR_Y + ROOM_H / 2, ROOM_BACK_Z);
    const cote = (s) => {
      const m = new THREE.Mesh(new THREE.PlaneGeometry(depth, ROOM_H, 32, 24), mat(2, T_ROOM - 0.006));
      m.rotation.y = s * Math.PI / 2;
      m.position.set(-s * W, FLOOR_Y + ROOM_H / 2, (ROOM_FRONT_Z + ROOM_BACK_Z) / 2);
      return m;
    };
    // the decor: a column, and a ledge along the back wall
    const colonne = new THREE.Mesh(new THREE.CylinderGeometry(0.85, 0.85, ROOM_H, 20), mat(3, T_DECOR));
    colonne.position.set(-W * 0.62, FLOOR_Y + ROOM_H / 2, ROOM_BACK_Z + 2.2);
    const rebord = new THREE.Mesh(new THREE.BoxGeometry(W * 0.7, 0.55, 1.2), mat(3, T_DECOR + 0.01));
    rebord.position.set(W * 0.42, FLOOR_Y + 3.4, ROOM_BACK_Z + 0.6);
    this._piece = [sol, fond, cote(1), cote(-1), colonne, rebord];
    for (const m of this._piece) { m.frustumCulled = false; this._prive.add(m); }
  }

  // --- the objects ---------------------------------------------------------------
  _construireObjets() {
    const formes = [
      () => new THREE.BoxGeometry(1, 1, 1),
      () => new THREE.OctahedronGeometry(0.72),
      () => new THREE.TorusGeometry(0.52, 0.19, 16, 28),
      () => new THREE.CylinderGeometry(0.42, 0.42, 1.05, 24),
      () => new THREE.IcosahedronGeometry(0.66),
      () => new THREE.DodecahedronGeometry(0.62),
      () => new THREE.TetrahedronGeometry(0.8)
    ];
    this._geos = formes.map((f) => avecAretes(f()));
    const n = this._plan.objects;
    this._objets = [];
    const orbites = [];
    for (let i = 0; i < n; i++) orbites.push(this._nouvelleOrbite(i, n));
    // shuffle the orbits, so size and place are independent
    for (let i = n - 1; i > 0; i--) {
      const j = Math.floor(this._hasard() * (i + 1));
      const a = orbites[i]; orbites[i] = orbites[j]; orbites[j] = a;
    }
    const bande = EDGE_BAND_PX * this._plan.capteur + 0.5;   // 2 sensor px at half resolution
    for (let i = 0; i < n; i++) {
      const f = lerp(SIZE_ECRAN[0], SIZE_ECRAN[1], (i + 0.5) / n);
      const masse = Math.pow((f - SIZE_ECRAN[0]) / (SIZE_ECRAN[1] - SIZE_ECRAN[0]), 0.9);
      const geo = this._geos[i % this._geos.length];
      const uni = {
        uTs: { value: 0.1 }, uTc: { value: 0.1 }, uCentre: { value: new THREE.Vector3() }, uRayon: { value: 1 },
        uHeatDir: { value: this._heatDir }, uBandePx: { value: bande }
      };
      const mesh = new THREE.Mesh(geo, new THREE.ShaderMaterial({
        uniforms: uni, vertexShader: OBJ_VERT, fragmentShader: OBJ_FRAG, precision: 'mediump'
      }));
      mesh.frustumCulled = false;
      this._prive.add(mesh);
      const o = {
        mesh, uni, f, masse, geo, s: 1,
        T: 0.1, Tc: 0.1, q: 0,
        tauCool: lerp(TAU_COOL[0], TAU_COOL[1], masse),
        tauHeat: lerp(TAU_HEAT[0], TAU_HEAT[1], masse),
        tauCore: lerp(TAU_CORE[0], TAU_CORE[1], masse),
        spin: new THREE.Vector3(this._hasard() - 0.5, this._hasard() - 0.5, this._hasard() - 0.5).multiplyScalar(0.8),
        orb: orbites[i], orbPrec: null, pos: new THREE.Vector3(), prec: new THREE.Vector3(),
        hazeAcc: 0, stampAt: -1e9
      };
      this._taille(o);
      this._objets.push(o);
    }
    this._reorg = -1;
  }

  // The world size that makes the object f of the frame's height at its depth.
  _taille(o) {
    const d = Math.max(4, CAMERA_DIST - o.orb.z);
    o.s = o.f * 2 * d * this._tanH;
    o.mesh.scale.setScalar(o.s);
    o.rayon = o.geo.boundingSphere.radius * o.s;
    o.uni.uRayon.value = o.rayon;
  }

  // An ellipse around the origin, as wide as the frame, in a plane kept close
  // to facing the camera: planes seen edge-on would carry objects straight
  // through the safe zone.
  _nouvelleOrbite(i, n) {
    const hH = CAMERA_DIST * this._tanH, hW = hH * this._aspect;
    const u = this._hasard();
    return {
      rx: hW * lerp(ORBIT_FX[0], ORBIT_FX[1], u),
      ry: hH * lerp(ORBIT_FY[0], ORBIT_FY[1], 1 - u * 0.5),
      tiltX: (this._hasard() * 2 - 1) * ORBIT_TILT,
      tiltY: (this._hasard() * 2 - 1) * ORBIT_TILT,
      w: (this._hasard() < 0.5 ? -1 : 1) * lerp(ORBIT_SPEED[0], ORBIT_SPEED[1], this._hasard()),
      phase: (i / n) * Math.PI * 2 + this._hasard() * 0.4,
      z: lerp(ORBIT_Z[0], ORBIT_Z[1], this._hasard())
    };
  }
  _surOrbite(o, t, out) {
    const a = o.phase + o.w * t;
    out.set(Math.cos(a) * o.rx, Math.sin(a) * o.ry, 0);
    const cy = Math.cos(o.tiltX), sy = Math.sin(o.tiltX);
    let y = out.y * cy, z = out.y * sy;
    const cx = Math.cos(o.tiltY), sx = Math.sin(o.tiltY);
    const x = out.x * cx + z * sx; z = -out.x * sx + z * cx;
    out.set(x, Math.max(FLOOR_Y + 0.6, y), z + o.z);
    return out;
  }

  // AND NONE IN THE CENTRE: an object whose silhouette would enter the safe
  // zone on screen is pushed out of it, in the camera's plane.
  _horsZone(p, rayon) {
    const cam = this._camera;
    const v = _v1.copy(p).applyMatrix4(cam.matrixWorldInverse);
    const prof = -v.z;
    if (prof < 0.5) return;
    const unite = prof * this._tanH;
    const sx = v.x / unite, sy = v.y / unite;
    // R19: the keep-out is the stage's block at the centre, not the cover's;
    // a zero half-size (no cover) still means no keep-out, as before.
    const c = safeZoneUniforms.uStageCenter.value, hs = safeZoneUniforms.uStageHalfSize.value;
    const marge = rayon / unite + 0.04;
    const hx = hs.x + marge, hy = hs.y + marge;
    const dx = sx - c.x, dy = sy - c.y;
    if ((hs.x > 0 || hs.y > 0) && Math.abs(dx) < hx && Math.abs(dy) < hy) {
      const k = Math.min(hx / Math.max(Math.abs(dx), 1e-4), hy / Math.max(Math.abs(dy), 1e-4));
      v.x += (dx * k - dx) * unite;
      v.y += (dy * k - dy) * unite;
    }
    // AND NONE OVER THE SCALE BAR: the readouts on the right are the one thing
    // on screen that must always be legible. An object whose silhouette would
    // reach them is held to their left.
    const aspect = safeZoneUniforms.uAspect.value || this._aspect;
    const limite = (BAR_X0 - 0.13) * aspect;          // in height units
    const droite = v.x / unite + rayon / unite;
    if (droite > limite) v.x -= (droite - limite) * unite;
    p.copy(v.applyMatrix4(cam.matrixWorld));
  }

  // --- the haze, the footprints, the wave -------------------------------------------
  _construireBrume() {
    const g = new THREE.BufferGeometry();
    this._hPos = new THREE.BufferAttribute(new Float32Array(HAZE_N * 3), 3);
    this._hT = new THREE.BufferAttribute(new Float32Array(HAZE_N), 1);
    this._hS = new THREE.BufferAttribute(new Float32Array(HAZE_N), 1);
    for (const a of [this._hPos, this._hT, this._hS]) a.setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('position', this._hPos); g.setAttribute('aT', this._hT); g.setAttribute('aSize', this._hS);
    this._hazeUni = { uPxPerUnit: { value: 400 } };
    this._brume = new THREE.Points(g, new THREE.ShaderMaterial({
      uniforms: this._hazeUni, vertexShader: HAZE_VERT, fragmentShader: HAZE_FRAG,
      transparent: true, depthWrite: false, depthTest: true,
      blending: THREE.CustomBlending, blendEquation: THREE.MaxEquation,
      blendSrc: THREE.OneFactor, blendDst: THREE.OneFactor, precision: 'mediump'
    }));
    this._brume.frustumCulled = false;
    this._brume.renderOrder = 3;
    this._prive.add(this._brume);
    this._h = { age: new Float32Array(HAZE_N).fill(-1), t0: new Float32Array(HAZE_N), s0: new Float32Array(HAZE_N) };
    this._hNext = 0;
    this._hazeEmis = 0;
  }
  _construireEmpreintes() {
    this._stamps = [];
    // each footprint is three flattened copies: the shape at its temperature,
    // and two slightly larger ones at 55% and 22% of its excess, under MAX
    // blending - a soft edge about four pixels wide instead of a hard, stepped
    // one (a flattened silhouette seen at a grazing angle stair-stepped).
    for (let i = 0; i < STAMP_N * 3; i++) {
      const uni = { uT: { value: 0 } };
      const m = new THREE.Mesh(this._geos[0], new THREE.ShaderMaterial({
        uniforms: uni, vertexShader: PLAIN_VERT, fragmentShader: STAMP_FRAG,
        side: THREE.DoubleSide, depthWrite: false, depthTest: true,
        blending: THREE.CustomBlending, blendEquation: THREE.MaxEquation,
        blendSrc: THREE.OneFactor, blendDst: THREE.OneFactor, precision: 'mediump'
      }));
      m.matrixAutoUpdate = false;
      m.frustumCulled = false;
      m.visible = false;
      m.renderOrder = 1;
      this._prive.add(m);
      this._stamps.push({ m, uni, age: -1, t0: 0, k: [1, 0.55, 0.22][i % 3], marge: [0, 0.07, 0.15][i % 3] });
    }
    this._stampNext = 0;
  }
  _construireOnde() {
    this._ondeUni = { uT: { value: 0 }, uThick: { value: 0.08 } };
    this._ondeMesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), new THREE.ShaderMaterial({
      uniforms: this._ondeUni, vertexShader: UV_VERT, fragmentShader: RING_FRAG,
      transparent: true, depthWrite: false, depthTest: false,
      blending: THREE.CustomBlending, blendEquation: THREE.MaxEquation,
      blendSrc: THREE.OneFactor, blendDst: THREE.OneFactor, precision: 'mediump'
    }));
    this._ondeMesh.frustumCulled = false;
    this._ondeMesh.renderOrder = 4;
    this._ondeMesh.visible = false;
    this._prive.add(this._ondeMesh);
  }

  // --- the readouts ------------------------------------------------------------------
  _creerTexte(opacite, visible) {
    const c = typeof document !== 'undefined' ? document.createElement('canvas') : null;
    const tex = c ? new THREE.CanvasTexture(c) : new THREE.DataTexture(new Uint8Array(4), 1, 1);
    tex.minFilter = THREE.LinearFilter; tex.magFilter = THREE.LinearFilter; tex.generateMipmaps = false;
    const uni = { uTex: { value: tex }, uRect: { value: new THREE.Vector4(0, 0, 0, 0) }, uOpacity: { value: opacite } };
    const m = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: uni, vertexShader: TEXT_VERT, fragmentShader: TEXT_FRAG,
      transparent: true, depthTest: false, depthWrite: false, precision: 'mediump'
    }));
    m.frustumCulled = false;
    m.renderOrder = -98;
    m.visible = visible && !!c;
    this._scene.add(m);
    return { canvas: c, tex, uni, m };
  }
  // A canvas sized in device pixels and a quad placed on whole device pixels.
  // (x, y) is the canvas's top-left corner in device pixels from the frame's top-left.
  _poserTexte(t, W, H, x, y, w, h, dessiner) {
    if (!t.canvas) return;
    w = Math.ceil(w); h = Math.ceil(h); x = Math.round(x); y = Math.round(y);
    if (t.canvas.width !== w || t.canvas.height !== h) { t.canvas.width = w; t.canvas.height = h; }
    const g = t.canvas.getContext('2d');
    g.clearRect(0, 0, w, h);
    dessiner(g);
    t.tex.needsUpdate = true;
    t.uni.uRect.value.set(2 * x / W - 1, 1 - 2 * (y + h) / H, 2 * w / W, 2 * h / H);
  }
  _majTextes(W, H, k, forcer) {
    const T = this._textes;
    if (forcer) {
      // THE SCALE'S FIVE VALUES, 14 px, at the five long graduations
      const fs = 14 * k, pad = 4 * k;
      const barX = (BAR_X0 + 1) / 2 * W, top = (1 - BAR_Y1) / 2 * H, bot = (1 - BAR_Y0) / 2 * H;
      const lw = fs * 5.2, x = barX - 0.020 / 2 * W - pad - lw;
      this._poserTexte(T.echelle, W, H, x, top - fs, lw, bot - top + 2 * fs, (g) => {
        g.font = `${fs}px ${POLICE}`;
        g.fillStyle = '#fff'; g.textAlign = 'right'; g.textBaseline = 'middle';
        for (let i = 0; i <= 4; i++) {
          const ty = fs + (bot - top) * (1 - i / 4);
          g.fillText(enDegres(i / 4).toFixed(1) + '°', lw, ty);
        }
      });
      // THE AIMING CROSS at the frame's centre, with its label
      const cs = 30 * k;
      this._poserTexte(T.visee, W, H, W / 2 - cs / 2, H / 2 - cs / 2, cs * 2.4, cs, (g) => {
        g.strokeStyle = '#fff'; g.lineWidth = Math.max(1, Math.round(k));
        const c = cs / 2, gap = 3.5 * k, arm = 11 * k;
        g.beginPath();
        g.moveTo(c - arm, c); g.lineTo(c - gap, c); g.moveTo(c + gap, c); g.lineTo(c + arm, c);
        g.moveTo(c, c - arm); g.lineTo(c, c - gap); g.moveTo(c, c + gap); g.lineTo(c, c + arm);
        g.stroke();
        g.font = `${11 * k}px ${POLICE}`; g.fillStyle = '#fff'; g.textBaseline = 'top';
        g.fillText('Sp1', c + arm + 3 * k, c + 2 * k);
      });
    }
    // THE HEADER, top left: the spot's reading and the emissivity, 12 px
    const fs = 12 * k, m = 16 * k;
    const texte = 'Sp1  ' + enDegres(this._spot).toFixed(1) + '°C';
    if (!forcer && texte === this._spotAffiche) return;
    this._spotAffiche = texte;
    this._poserTexte(T.entete, W, H, m, m, fs * 9, fs * 2.8, (g) => {
      g.font = `${fs}px ${POLICE}`; g.fillStyle = '#fff'; g.textBaseline = 'top';
      g.fillText(texte, 0, 0);
      g.fillText('ε 0.95', 0, fs * 1.45);
    });
  }

  // The spot: what the cross at the centre is aimed at, found on the CPU - a
  // readback from the GPU would stall the frame. The nearest object on the
  // line of sight, read between its core and its surface, else the room.
  _mesurerSpot(dt) {
    const cam = this._camera;
    const fwd = _v2.set(0, 0, -1).applyQuaternion(cam.quaternion);
    let best = null, bd = Infinity, t = T_ROOM + 0.01;
    for (const o of this._objets) {
      const v = _v1.copy(o.pos).sub(cam.position);
      const along = v.dot(fwd);
      if (along <= 0) continue;
      const perp = v.sub(_v3.copy(fwd).multiplyScalar(along)).length();
      if (perp < o.rayon * 0.75 && along < bd) { bd = along; best = o; t = lerp(o.Tc, o.T, perp / o.rayon); }
    }
    this._spot += (t - this._spot) * Math.min(1, dt / 0.25);
  }

  _rendreTemperature(renderer) {
    renderer.getDrawingBufferSize(_taille);
    const W = Math.max(1, _taille.x), H = Math.max(1, _taille.y);
    this._barreUni.uPx.value.set(2 / W, 2 / H);
    // device pixels per CSS pixel, for the text
    const css = renderer.domElement && renderer.domElement.clientHeight;
    const k = css > 0 ? H / css : (typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);
    const cle = W + 'x' + H + '@' + k.toFixed(3);
    if (cle !== this._pxVus) { this._pxVus = cle; this._k = k; this._majTextes(W, H, k, true); }
    else if (this._temps - this._spotAt > 0.25) { this._spotAt = this._temps; this._majTextes(W, H, k, false); }
    const w = Math.max(64, Math.round(W * this._plan.capteur)), h = Math.max(64, Math.round(H * this._plan.capteur));
    if (this._rt.width !== w || this._rt.height !== h) this._rt.setSize(w, h);
    this._uni.uTexel.value.set(1 / w, 1 / h);
    if (this._sansScene) this._prive.visible = false;
    if (this._hazeUni) this._hazeUni.uPxPerUnit.value = h / (2 * this._tanH);
    const prevTarget = renderer.getRenderTarget();
    const prevAuto = renderer.autoClear;
    renderer.getClearColor(_couleur);
    const prevAlpha = renderer.getClearAlpha();
    const fond = T_BG + this._air;
    renderer.setRenderTarget(this._rt);
    renderer.setClearColor(_c2.setRGB(fond, fond, fond, THREE.LinearSRGBColorSpace), 1);
    renderer.autoClear = true;
    renderer.render(this._prive, this._camera);
    renderer.setRenderTarget(prevTarget);
    renderer.setClearColor(_couleur, prevAlpha);
    renderer.autoClear = prevAuto;
  }

  // --- the camera ------------------------------------------------------------------
  // A slow orbit - an arc swept back and forth - and the small shake of a
  // hand-held imager. The WHOLE position and orientation, every frame.
  _placerCamera(t) {
    const a = Math.sin(t * Math.PI * 2 / CAMERA_PERIOD) * CAMERA_SWEEP;
    const n1 = Math.sin(t * 2.3) * 0.6 + Math.sin(t * 3.7 + 1.3) * 0.4;
    const n2 = Math.sin(t * 1.9 + 0.7) * 0.6 + Math.sin(t * 4.3 + 2.1) * 0.4;
    const cam = this._camera;
    cam.position.set(Math.sin(a) * CAMERA_DIST + n1 * SHAKE_POS, 1.2 + n2 * SHAKE_POS, Math.cos(a) * CAMERA_DIST);
    cam.lookAt(0, -0.4, 0);
    cam.rotateZ(n1 * SHAKE_ROT);
    cam.rotateX(n2 * SHAKE_ROT);
    cam.updateMatrixWorld();
  }

  // --- heat -------------------------------------------------------------------------
  _chauffer(o, dq) { o.q += dq; }

  _choisirParBande(low, high) {
    const cible = low + high > 0.05 ? low / (low + high) : this._hasard();
    let tot = 0;
    const w = this._objets.map((o) => { const x = Math.exp(-Math.pow((o.masse - cible) / 0.25, 2)) + 0.02; tot += x; return x; });
    let r = this._hasard() * tot;
    for (let i = 0; i < w.length; i++) { r -= w[i]; if (r <= 0) return this._objets[i]; }
    return this._objets[this._objets.length - 1];
  }

  update(deltaTime, profile) {
    // PAUSE: the temperatures freeze. Nothing cools, nothing moves.
    if (profile.playbackState !== 'PLAYING') return;
    const dt = Math.min(deltaTime, 0.1);
    this._temps += dt;
    this._uni.uTime.value = this._temps;
    const director = profile.director;
    const bandes = (director && director.hasReliableGrid && director.bands) ? director.bands : null;
    const low = bandes ? clamp01(bandes.low) : 0;
    const mid = bandes ? clamp01(bandes.mid) : 0;
    const high = bandes ? clamp01(bandes.high) : 0;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    this._secondesBeat = bpm > 0 ? 60 / bpm : 0.5;

    if (profile.artworkPalette !== this._paletteVue) {
      this._paletteVue = profile.artworkPalette;
      const v = profile.artworkPalette && profile.artworkPalette.vividColors;
      const base = ironbow(T_ROOM);
      if (v && v.length) {
        const c = new THREE.Color(v[0]);
        const l = 0.2126 * base[0] + 0.7152 * base[1] + 0.0722 * base[2];
        const lc = Math.max(1e-3, 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b);
        this._uni.uTint.value.setRGB(c.r * l / lc, c.g * l / lc, c.b * l / lc);
      } else this._uni.uTint.value.setRGB(...base);
    }

    // the objects' rest temperature: cold at the start, warmer in the middle, cold at the end
    const prog = profile.duration > 0 ? clamp01(profile.progress || 0) : 0.5;
    this._ambiant = 0.1 + 0.2 * Math.sin(Math.PI * prog);

    let battement = false;
    const bp = profile.beatPhase;
    if (bp != null) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) battement = true;
      this._phaseBeat = bp;
    } else {
      this._libre += dt;
      if (this._libre >= this._secondesBeat) { this._libre -= this._secondesBeat; battement = true; }
    }
    let mesure = false;
    const bar = profile.barPhase;
    if (bar != null) {
      if (this._phaseBar != null && bar < this._phaseBar - 0.5) mesure = true;
      this._phaseBar = bar;
    } else if (battement && (this._battements + 1) % 4 === 0) mesure = true;
    let phrase = false;
    const ph = profile.phrasePhase;
    if (ph != null) {
      if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true;
      this._phasePhrase = ph;
    } else if (battement && (this._battements + 1) % 16 === 0) phrase = true;
    if (battement) this._battements++;

    if (battement) this._chauffer(this._choisirParBande(low, high), IMPULSE_BEAT);
    if (mesure) {
      const a = this._hasard() * Math.PI * 2;
      this._onde = {
        o: new THREE.Vector3(Math.cos(a) * 9, (this._hasard() - 0.5) * 5, Math.sin(a) * 3),
        t0: this._temps, v: 18 / Math.max(0.25, this._secondesBeat * 2), touches: new Set()
      };
    }
    if (phrase) {
      for (let i = 0; i < this._objets.length; i++) {
        const o = this._objets[i];
        o.orbPrec = o.orb; o.orb = this._nouvelleOrbite(i, this._objets.length);
      }
      this._reorg = this._temps;
    }
    const mode = director ? director.mode : null;
    if ((mode === 'drop' && this._modePrec !== 'drop') || this._dropForce) {
      this._dropForce = false;
      this._dropTenue = this._temps + this._secondesBeat;
      this._air = 0.20;
    }
    this._modePrec = mode;
    if (this._chaufferForce != null && this._objets[this._chaufferForce]) {
      const o = this._objets[this._chaufferForce];
      o.T = 1; o.q = 0;
    }

    this._placerCamera(this._temps);
    this._avancerOnde();
    this._avancerObjets(dt, mid);
    if (this._brume) this._avancerBrume(dt);
    if (this._stamps) this._avancerEmpreintes(dt);
    this._mesurerSpot(dt);
    this._air *= Math.exp(-dt / 1.0);
  }

  _avancerOnde() {
    const on = this._onde;
    if (!on) { if (this._ondeMesh) this._ondeMesh.visible = false; return; }
    const r = (this._temps - on.t0) * on.v;
    for (let i = 0; i < this._objets.length; i++) {
      const o = this._objets[i];
      if (on.touches.has(i)) continue;
      if (o.pos.distanceTo(on.o) <= r) { on.touches.add(i); this._chauffer(o, IMPULSE_WAVE); }
    }
    if (this._ondeMesh) {
      const vie = clamp01(r / 26);
      this._ondeMesh.visible = vie < 1;
      this._ondeMesh.position.copy(on.o);
      this._ondeMesh.quaternion.copy(this._camera.quaternion);
      this._ondeMesh.scale.setScalar(Math.max(0.01, r));
      this._ondeUni.uT.value = (T_ROOM + 0.16) * (1 - vie);
    }
    if (r > 34) this._onde = null;
  }

  _avancerObjets(dt, mid) {
    const O = this._objets, n = O.length;
    const tenue = this._temps < this._dropTenue;
    // DIFFUSION, by pairs, on the surfaces: a hot object warms its neighbours
    const k = this._sansDiffusion ? 0 : DIFFUSE_RATE * (0.6 + 1.4 * mid);
    for (let i = 0; i < n; i++) for (let j = i + 1; j < n; j++) {
      const a = O[i], b = O[j];
      const d = a.pos.distanceTo(b.pos);
      if (d > DIFFUSE_RADIUS) continue;
      const f = k * (1 - d / DIFFUSE_RADIUS) * (b.T - a.T) * dt;
      a.T += f / (0.4 + a.masse); b.T -= f / (0.4 + b.masse);
    }
    const reorg = this._reorg >= 0 ? clamp01((this._temps - this._reorg) / (REORG_BEATS * this._secondesBeat)) : 1;
    const e = reorg * reorg * (3 - 2 * reorg);
    for (let i = 0; i < n; i++) {
      const o = O[i];
      // heat arrives at the SURFACE, fast
      const inn = o.q * (1 - Math.exp(-dt / o.tauHeat));
      o.q -= inn; o.T += inn;
      if (tenue) { o.T = 1; o.Tc = 1; o.q = 0; }
      else {
        // the core soaks it up at its own pace; the surface gives up a share
        const x = (o.T - o.Tc) * (1 - Math.exp(-dt / o.tauCore));
        o.Tc += x; o.T -= x * CORE_SHARE;
        // and the surface loses it to the room
        o.T += (this._ambiant - o.T) * (1 - Math.exp(-dt / o.tauCool));
      }
      o.T = clamp01(o.T); o.Tc = clamp01(o.Tc);
      o.uni.uTs.value = o.T; o.uni.uTc.value = o.Tc;
      o.prec.copy(o.pos);
      this._surOrbite(o.orb, this._temps, o.pos);
      if (e < 1 && o.orbPrec) { this._surOrbite(o.orbPrec, this._temps, _v2); o.pos.lerpVectors(_v2, o.pos, e); }
      this._horsZone(o.pos, o.rayon);
      o.mesh.position.copy(o.pos);
      o.uni.uCentre.value.copy(o.pos);
      o.mesh.rotation.x += o.spin.x * dt; o.mesh.rotation.y += o.spin.y * dt; o.mesh.rotation.z += o.spin.z * dt;
      // the room feels it
      this._roomObj[i].set(o.pos.x, o.pos.y, o.pos.z, Math.max(0, (o.T + o.Tc) / 2 - T_ROOM));
    }
    if (reorg >= 1) {
      if (this._reorg >= 0) for (const o of O) this._taille(o);   // sizes follow the new depths
      this._reorg = -1;
    }
  }

  _avancerBrume(dt) {
    const H = this._h, pos = this._hPos.array, T = this._hT.array, S = this._hS.array;
    // a hot object in motion leaves warm air behind it, which rises
    for (const o of this._objets) {
      if (o.T < this._ambiant + HAZE_OVER || o.pos.distanceTo(o.prec) < 1e-4) { o.hazeAcc = 0; continue; }
      o.hazeAcc += dt;
      while (o.hazeAcc >= HAZE_EVERY) {
        o.hazeAcc -= HAZE_EVERY;
        const i = this._hNext; this._hNext = (this._hNext + 1) % HAZE_N;
        H.age[i] = 0; H.t0[i] = T_ROOM + (o.T - T_ROOM) * 0.70; H.s0[i] = o.s * 0.55;
        // behind the object, along its motion
        _v1.copy(o.pos).sub(o.prec); const l = _v1.length() || 1;
        const r = o.rayon * 0.9 / l;
        pos[i * 3] = o.pos.x - _v1.x * r; pos[i * 3 + 1] = o.pos.y - _v1.y * r + o.rayon * 0.3; pos[i * 3 + 2] = o.pos.z - _v1.z * r;
        this._hazeEmis++;
      }
    }
    for (let i = 0; i < HAZE_N; i++) {
      if (H.age[i] < 0) { T[i] = 0; continue; }
      H.age[i] += dt;
      const u = H.age[i] / HAZE_LIFE;
      if (u >= 1) { H.age[i] = -1; T[i] = 0; continue; }
      T[i] = H.t0[i] * Math.pow(1 - u, 1.4);
      S[i] = H.s0[i] * (1 + 2.2 * u);
      pos[i * 3 + 1] += dt * HAZE_RISE;
    }
    this._hPos.needsUpdate = this._hT.needsUpdate = this._hS.needsUpdate = true;
  }

  _avancerEmpreintes(dt) {
    const floorT = T_ROOM + 0.004;
    for (const o of this._objets) {
      const h = o.pos.y - o.rayon - FLOOR_Y;
      if (h > STAMP_REACH || this._temps - o.stampAt < STAMP_EVERY) continue;
      const t0 = floorT + (o.T - floorT) * 0.85 * (1 - Math.max(0, h) / STAMP_REACH);
      if (t0 < floorT + 0.04) continue;
      o.stampAt = this._temps;
      const base = this._stampNext; this._stampNext = (this._stampNext + 3) % (STAMP_N * 3);
      for (let c = 0; c < 3; c++) {
        const s = this._stamps[base + c];
        // the object's own shape, flattened onto the floor under it:
        // M = translate * flatten * rotation * size
        s.m.geometry = o.geo;
        _m1.compose(_v0, o.mesh.quaternion, _v3.setScalar(o.s));
        const g = 1 + s.marge / Math.max(0.3, o.s);
        _m2.makeScale(g, 0.0005, g); _m1.premultiply(_m2);
        _m2.makeTranslation(o.pos.x, FLOOR_Y + 0.02, o.pos.z); _m1.premultiply(_m2);
        s.m.matrix.copy(_m1);
        s.m.matrixWorldNeedsUpdate = true;
        s.age = 0; s.t0 = floorT + (t0 - floorT) * s.k; s.m.visible = true;
      }
    }
    for (const s of this._stamps) {
      if (s.age < 0) continue;
      s.age += dt;
      const u = s.age / STAMP_LIFE;
      if (u >= 1) { s.age = -1; s.m.visible = false; continue; }
      s.uni.uT.value = floorT + (s.t0 - floorT) * Math.pow(1 - u, 1.3);
    }
  }

  // --- for the probes ----------------------------------------------------------------
  get objets() { return this._objets.map((o) => ({ T: o.T, Tc: o.Tc, masse: o.masse, s: o.s, f: o.f, rayon: o.rayon, tauCool: o.tauCool, pos: o.pos.clone() })); }
  get aretes() { return this._geos.map((g) => ({ triangles: g.getAttribute('position').count / 3, aretes: g.userData.aretesReelles })); }
  get brume() { return { emis: this._hazeEmis || 0, vivants: this._h ? Array.from(this._h.age).filter((a) => a >= 0).length : 0 }; }
  get empreintes() { return this._stamps ? this._stamps.filter((s) => s.age >= 0 && s.k === 1).length : 0; }
  get spot() { return this._spot; }

  dispose() {
    this._ecran.onBeforeRender = null;
    for (const m of [this._ecran, this._barreMesh]) { this._scene.remove(m); m.geometry.dispose(); m.material.dispose(); }
    for (const t of Object.values(this._textes)) { this._scene.remove(t.m); t.m.geometry.dispose(); t.m.material.dispose(); t.tex.dispose(); }
    this._prive.traverse((o) => {
      if (o.material) o.material.dispose();
      if (o.geometry && !this._geos.includes(o.geometry)) o.geometry.dispose();
    });
    for (const g of this._geos) g.dispose();
    this._rt.dispose();
    this._rampTex.dispose();
    this._prive = null; this._objets = null;
  }
}

const _v0 = new THREE.Vector3();
const _v1 = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _v3 = new THREE.Vector3();
const _m1 = new THREE.Matrix4();
const _m2 = new THREE.Matrix4();
const _couleur = new THREE.Color();
const _c2 = new THREE.Color();

export default ThermalVisualizer;
